import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SquaresFour, Folder, Gear, SignOut, UserCircle, FolderOpen, Brain } from '@phosphor-icons/react';
import { logout, getUser } from '@/utils/auth';

const Navbar = ({ variant = 'dark' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  const handleLogout = () => {
    logout();
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', icon: SquaresFour, label: 'Dashboard' },
    { path: '/my-creations', icon: FolderOpen, label: 'My Projects' },
    { path: '/business-brain', icon: Brain, label: 'Business Brain' },
    { path: '/settings', icon: Gear, label: 'Settings' },
  ];

  const isDark = variant === 'dark';

  return (
    <nav
      className={`${isDark ? 'bg-slate-900/80 backdrop-blur-lg border-white/10' : 'bg-white border-slate-200'} border-b sticky top-0 z-50 pt-safe pl-safe pr-safe`}
      data-testid="app-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Always navigates to dashboard */}
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity" 
            data-testid="navbar-logo"
            onClick={(e) => {
              // Ensure navigation works even if already on dashboard
              if (location.pathname === '/dashboard') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>PayGBot</span>
          </Link>

          {/* Nav Items */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? isDark 
                        ? 'bg-white/10 text-white'
                        : 'bg-blue-50 text-blue-600'
                      : isDark
                        ? 'text-slate-400 hover:bg-white/5 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon size={20} weight="duotone" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            <div className={`hidden sm:flex items-center space-x-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              <UserCircle size={24} weight="duotone" />
              <span data-testid="user-name">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isDark 
                  ? 'text-slate-400 hover:text-white hover:bg-white/5'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <SignOut size={20} weight="duotone" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <div className={`md:hidden ${isDark ? 'border-white/10' : 'border-slate-200'} border-t`}>
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  isActive(item.path)
                    ? isDark ? 'text-white' : 'text-blue-600'
                    : isDark ? 'text-slate-500' : 'text-slate-600'
                }`}
              >
                <Icon size={24} weight="duotone" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
